import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { ClipboardCheck, TrendingUp, Banknote, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ValueStorySlidesProps {
  onComplete: () => void;
  onSkip: () => void;
}

interface SlideData {
  id: number;
  icon: React.ReactNode;
  titleKey: string;
  gradient: string;
  iconBg: string;
}

const slides: SlideData[] = [
  {
    id: 0,
    icon: <ClipboardCheck className="w-20 h-20" strokeWidth={1.5} />,
    titleKey: 'onboarding.valueStory.capture',
    gradient: 'from-success/30 via-success/10 to-background',
    iconBg: 'bg-success/20',
  },
  {
    id: 1,
    icon: <TrendingUp className="w-20 h-20" strokeWidth={1.5} />,
    titleKey: 'onboarding.valueStory.grow',
    gradient: 'from-info/30 via-info/10 to-background',
    iconBg: 'bg-info/20',
  },
  {
    id: 2,
    icon: <Banknote className="w-20 h-20" strokeWidth={1.5} />,
    titleKey: 'onboarding.valueStory.earn',
    gradient: 'from-accent/30 via-accent/10 to-background',
    iconBg: 'bg-accent/20',
  },
];

const swipeConfidenceThreshold = 10000;
const swipePower = (offset: number, velocity: number) => {
  return Math.abs(offset) * velocity;
};

export const ValueStorySlides = ({ onComplete, onSkip }: ValueStorySlidesProps) => {
  const { t } = useTranslation();
  const [[page, direction], setPage] = useState([0, 0]);

  const paginate = (newDirection: number) => {
    const newPage = page + newDirection;
    if (newPage >= 0 && newPage < slides.length) {
      setPage([newPage, newDirection]);
    }
  };

  const handleDragEnd = (e: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const swipe = swipePower(info.offset.x, info.velocity.x);

    if (swipe < -swipeConfidenceThreshold) {
      paginate(1);
    } else if (swipe > swipeConfidenceThreshold) {
      paginate(-1);
    }
  };

  const handleNext = () => {
    if (page < slides.length - 1) {
      paginate(1);
    } else {
      onComplete();
    }
  };

  const isLastSlide = page === slides.length - 1;
  const currentSlide = slides[page];

  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 300 : -300,
      opacity: 0,
    }),
  };

  return (
    <div className={`min-h-screen flex flex-col bg-gradient-to-b ${currentSlide.gradient}`}>
      {/* Skip button */}
      <div className="flex justify-end p-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onSkip}
          className="text-muted-foreground"
        >
          {t('onboarding.valueStory.skip')}
          <X className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {/* Slide content */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 overflow-hidden">
        <AnimatePresence initial={false} custom={direction} mode="wait">
          <motion.div
            key={page}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: 'spring', stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 },
            }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            onDragEnd={handleDragEnd}
            className="flex flex-col items-center text-center cursor-grab active:cursor-grabbing"
          >
            {/* Icon container with animation */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
              className={`${currentSlide.iconBg} rounded-full p-8 mb-8`}
            >
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                className="text-foreground"
              >
                {currentSlide.icon}
              </motion.div>
            </motion.div>

            {/* Title */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-2xl font-bold text-foreground max-w-xs"
            >
              {t(currentSlide.titleKey)}
            </motion.h2>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Dot indicators */}
      <div className="flex justify-center gap-2 mb-8">
        {slides.map((slide, index) => (
          <motion.button
            key={slide.id}
            onClick={() => setPage([index, index > page ? 1 : -1])}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === page 
                ? 'w-8 bg-foreground' 
                : 'w-2 bg-foreground/30'
            }`}
            whileTap={{ scale: 0.9 }}
          />
        ))}
      </div>

      {/* Next / Continue button */}
      <div className="px-6 pb-8 safe-area-inset-bottom">
        <Button
          onClick={handleNext}
          className="w-full py-6 text-lg font-semibold rounded-xl"
          size="lg"
        >
          {isLastSlide 
            ? t('onboarding.valueStory.continue') 
            : t('onboarding.valueStory.next')
          }
        </Button>
      </div>
    </div>
  );
};
